import type { MessageFormatElement } from "@formatjs/icu-messageformat-parser";
import IntlMessageFormat from "intl-messageformat";
import type { Keys as DiscoverKeys } from "../discover/app/locales/types";
import I18nContext from "./context";
import type {  Keys as PortalKeys } from "./locales/types";
import { Locale } from "./locales/types";


type Keys = PortalKeys & DiscoverKeys
export type Key = keyof Keys;

//function _<K extends Key>(id: K, ...args: Keys[K] extends never ? never : [Keys[K]]): string {
//@ts-ignore
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function _<K extends Key>(id: K, ...args: any | never): string {
	return translator.translate(id, ...args);
}

function _any(id: string, args: Record<string, string> = {}): string {
	return translator.translateAny(id, args);
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function _styled<K extends Key>(id: K, ...args: any | never) {
	return translator.translateStyled(id, ...args);
}

const locales = Object.values(Locale);

function isValidLang(lang: string): boolean {
	return locales.includes(lang as Locale);
}

function isValidDiscoverLang(lang: string): boolean {
	return lang === Locale.EN
}
class Translator {
	locale: Locale;

	private translations: Map<Locale, Map<Key, string | MessageFormatElement[]>>;

	constructor(defaultLocale: Locale = locales[0]) {
		this.locale = defaultLocale;
		this.translations = new Map();
	}

	add(
		locale: Locale,
		translations: Record<Key, string | { type: number; value: string }[]>,
	) {
		this.translations.set(
			locale,
			new Map(Object.entries(translations) as [Key, string][]),
		);
	}

	private getTranslation(id: string) {
		const translation = this.translations
			.get(this.locale)
			?.get(id as keyof Keys);
		if (
			!translation ||
			translation === "" ||
			(Array.isArray(translation) && translation.length === 0)
		) {
			return undefined;
		}

		return translation;
	}

	translateAny(id: string, args: Record<string, string> = {}): string {
		const data = args;
		const translation = this.getTranslation(id);

		if (!translation) {
			return id;
		}

		const msg = new IntlMessageFormat(translation, this.locale);
		return msg.format(data).toString();
	}

	translate<K extends Key>(
		id: K,
		...args: Keys[K] extends never ? never : [Keys[K]]
	): string {
		const msg = this.translateStyled(id, ...args);
		return msg.toString();
	}

	translateStyled<K extends Key>(
		id: K,
		...args: Keys[K] extends never ? never : [Keys[K]]
	) {
		const data = args[0];
		const translation = this.getTranslation(id);

		if (!translation) {
			try {
				return id.toString();
			} catch (e) {
				console.log(`missing toString`, id);
				return 'Missing translation key';
			}
		}

		try {
			const msg = new IntlMessageFormat(translation, this.locale);

			return msg.format(data);
		} catch(e) {
			if(e instanceof Error) {
				return `Translation error: ${e.message}, id: ${id}, locale: ${this.locale}`;
			} else {
				return "Unknown translation error";
			}
		}
	}

	changeLocale(locale: Locale) {
		this.locale = locale;
	}

	hasLocale(locale: Locale): boolean {
		return this.translations.has(locale);
	}
}

const translator = new Translator();
const locale = translator.locale;
export type TranslationFunction = typeof _;
export {
	Locale,
	Translator,
	translator,
	_,
	_any,
	_styled,
	isValidLang,
	isValidDiscoverLang,
	I18nContext,
	locale,
};
